import React, { useState, useEffect } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { FileText, Tag, CheckCircle, Clock, Award, ArrowLeft, Shield, AlertTriangle } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Helper function to check if content contains HTML
const containsHtml = (str) => {
  if (!str) return false;
  return /<[a-z][\s\S]*>/i.test(str);
};

// Helper function to sanitize HTML and fix common issues
const sanitizeHtml = (html) => {
  if (!html) return '';
  
  try {
    // Create a temporary div to parse the HTML
    const temp = document.createElement('div');
    temp.innerHTML = html;
    
    // Remove any script tags for security
    const scripts = temp.querySelectorAll('script');
    scripts.forEach(s => s.remove());
    
    // Remove any onclick, onerror, etc. event handlers
    const allElements = temp.querySelectorAll('*');
    allElements.forEach(el => {
      Array.from(el.attributes).forEach(attr => {
        if (attr.name.startsWith('on')) {
          el.removeAttribute(attr.name);
        }
      });
    });
    
    return temp.innerHTML;
  } catch (e) {
    console.error('Error sanitizing HTML:', e);
    // If parsing fails, strip all HTML and return plain text
    return html.replace(/<[^>]*>/g, '');
  }
};

// Error boundary component for safe HTML rendering
const SafeHtmlRenderer = ({ html, className }) => {
  const [hasError, setHasError] = useState(false);
  const [sanitizedHtml, setSanitizedHtml] = useState('');

  useEffect(() => {
    try {
      const clean = sanitizeHtml(html);
      setSanitizedHtml(clean);
      setHasError(false);
    } catch (e) {
      console.error('Error processing HTML:', e);
      setHasError(true);
    }
  }, [html]);

  if (hasError || !sanitizedHtml) {
    // Fallback to plain text if HTML processing fails
    const plainText = html ? html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim() : '';
    return <p className={className}>{plainText}</p>;
  }

  return (
    <div 
      className={className}
      dangerouslySetInnerHTML={{ __html: sanitizedHtml }}
    />
  );
};

export default function ViewQuestion() {
  const { questionId } = useParams();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');
  
  const [question, setQuestion] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchQuestion = async () => {
      try {
        setLoading(true);
        const authToken = localStorage.getItem('token');
        
        // Use public-view endpoint if token is provided, otherwise use authenticated view
        let url;
        let config = {};
        
        if (token) {
          // Public access with token - no auth required
          url = `${API}/interview-questions/${questionId}/public-view?token=${token}`;
        } else if (authToken) {
          // Authenticated user
          url = `${API}/interview-questions/${questionId}/view`;
          config = { headers: { Authorization: `Bearer ${authToken}` } };
        } else {
          // No token and not logged in
          setError('Access token required. Please use the link from your email.');
          setLoading(false);
          return;
        }
        
        const response = await axios.get(url, config);
        
        if (response.data.has_access) {
          setQuestion(response.data.question);
        } else {
          setError('You do not have permission to view this question.');
        }
      } catch (err) {
        setError(err.response?.data?.detail || 'Failed to load question. You may not have access.');
      } finally {
        setLoading(false);
      }
    };

    fetchQuestion();
  }, [questionId, token]);

  // Prevent screenshots - Enhanced protection for desktop/laptop
  // Note: Reduced aggressiveness on mobile to prevent blank page issues
  useEffect(() => {
    // Detect if mobile device
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    
    // Disable right-click
    const handleContextMenu = (e) => e.preventDefault();
    
    // Disable print screen and screenshot shortcuts (Windows, Mac, Linux)
    const handleKeyDown = (e) => {
      // PrintScreen, Snipping Tool shortcuts, Mac screenshot shortcuts
      if (e.key === 'PrintScreen' || 
          (e.ctrlKey && e.key === 'p') || 
          (e.ctrlKey && e.shiftKey && e.key === 's') ||
          (e.ctrlKey && e.shiftKey && e.key === '4') ||
          (e.metaKey && e.shiftKey && e.key === '4') ||
          (e.metaKey && e.shiftKey && e.key === '3') ||
          (e.metaKey && e.shiftKey && e.key === '5') ||
          // Windows Snipping Tool shortcuts
          (e.key === 's' && e.shiftKey && (e.metaKey || e.ctrlKey)) ||
          // F12 DevTools
          e.key === 'F12' ||
          // Ctrl+Shift+I (DevTools)
          (e.ctrlKey && e.shiftKey && e.key === 'i') ||
          (e.ctrlKey && e.shiftKey && e.key === 'I') ||
          // Ctrl+Shift+C (Inspect Element)
          (e.ctrlKey && e.shiftKey && e.key === 'c') ||
          (e.ctrlKey && e.shiftKey && e.key === 'C') ||
          // Ctrl+U (View Source)
          (e.ctrlKey && e.key === 'u') ||
          (e.ctrlKey && e.key === 'U')) {
        e.preventDefault();
        e.stopPropagation();
        alert('This action is disabled for security reasons.');
        return false;
      }
    };

    // Disable selection
    const handleSelectStart = (e) => e.preventDefault();
    
    // Add blur effect when window loses focus - DESKTOP ONLY
    const handleVisibilityChange = () => {
      if (isMobile) return; // Skip blur on mobile
      
      const container = document.getElementById('protected-content');
      if (container) {
        if (document.hidden) {
          container.style.filter = 'blur(30px)';
          container.style.opacity = '0.3';
        } else {
          container.style.filter = 'none';
          container.style.opacity = '1';
        }
      }
    };

    // Blur when window loses focus - DESKTOP ONLY
    let hasInteracted = false;
    const handleInteraction = () => { hasInteracted = true; };
    
    const handleWindowBlur = () => {
      if (isMobile) return; // Skip blur on mobile - causes blank page issues
      if (!hasInteracted) return;
      
      const container = document.getElementById('protected-content');
      if (container) {
        container.style.filter = 'blur(30px)';
        container.style.opacity = '0.3';
        container.style.transition = 'filter 0.05s ease, opacity 0.05s ease';
      }
    };

    const handleWindowFocus = () => {
      const container = document.getElementById('protected-content');
      if (container) {
        // Small delay before removing blur to catch quick screenshots
        setTimeout(() => {
          if (document.hasFocus()) {
            container.style.filter = 'none';
            container.style.opacity = '1';
          }
        }, isMobile ? 0 : 200); // No delay on mobile
      }
    };

    // Disable drag events
    const handleDragStart = (e) => e.preventDefault();
    
    // DevTools detection - DESKTOP ONLY
    const detectDevTools = () => {
      if (isMobile) return; // Skip on mobile
      
      const widthThreshold = window.outerWidth - window.innerWidth > 160;
      const heightThreshold = window.outerHeight - window.innerHeight > 160;
      const container = document.getElementById('protected-content');
      
      if ((widthThreshold || heightThreshold) && container) {
        container.style.filter = 'blur(30px)';
        container.style.opacity = '0.3';
      }
    };

    // Check for DevTools on resize - DESKTOP ONLY
    const handleResize = () => {
      if (isMobile) return;
      if (hasInteracted) {
        detectDevTools();
      }
    };
    
    // Ensure content is visible on initial load
    setTimeout(() => {
      const container = document.getElementById('protected-content');
      if (container) {
        container.style.filter = 'none';
        container.style.opacity = '1';
      }
    }, 100);

    // Add CSS to prevent screenshots via media queries
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        #protected-content { display: none !important; }
        body::after { 
          content: 'Printing is disabled for this content.'; 
          display: block; 
          text-align: center; 
          padding: 50px; 
          font-size: 24px; 
        }
      }
      /* Attempt to detect screen capture - blur when not actively viewed */
      #protected-content {
        -webkit-user-select: none !important;
        -moz-user-select: none !important;
        -ms-user-select: none !important;
        user-select: none !important;
      }
      /* Make images non-draggable */
      #protected-content img {
        -webkit-user-drag: none;
        -khtml-user-drag: none;
        -moz-user-drag: none;
        -o-user-drag: none;
        user-drag: none;
        pointer-events: none;
      }
    `;
    document.head.appendChild(style);

    document.addEventListener('contextmenu', handleContextMenu);
    document.addEventListener('keydown', handleKeyDown, true);
    document.addEventListener('selectstart', handleSelectStart);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    document.addEventListener('dragstart', handleDragStart);
    document.addEventListener('click', handleInteraction);
    document.addEventListener('scroll', handleInteraction);
    document.addEventListener('mousemove', handleInteraction);
    window.addEventListener('blur', handleWindowBlur);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('resize', handleResize);

    // Periodic check for DevTools
    const devToolsInterval = setInterval(detectDevTools, 1000);

    return () => {
      document.removeEventListener('contextmenu', handleContextMenu);
      document.removeEventListener('keydown', handleKeyDown, true);
      document.removeEventListener('selectstart', handleSelectStart);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      document.removeEventListener('dragstart', handleDragStart);
      document.removeEventListener('click', handleInteraction);
      document.removeEventListener('scroll', handleInteraction);
      document.removeEventListener('mousemove', handleInteraction);
      window.removeEventListener('blur', handleWindowBlur);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('resize', handleResize);
      clearInterval(devToolsInterval);
      if (style.parentNode) {
        style.parentNode.removeChild(style);
      }
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="h-12 w-12 border-4 border-emerald-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Loading question...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-4">
        <Card className="max-w-md w-full shadow-xl">
          <CardContent className="pt-6 text-center">
            <div className="h-16 w-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <AlertTriangle className="h-8 w-8 text-red-500" />
            </div>
            <h2 className="text-xl font-bold text-gray-800 mb-2">Access Denied</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button onClick={() => navigate('/')} className="bg-emerald-600 hover:bg-emerald-700">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Go to Homepage
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div 
      className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 py-8 px-4 protected-view relative"
      style={{ 
        userSelect: 'none', 
        WebkitUserSelect: 'none', 
        MozUserSelect: 'none', 
        msUserSelect: 'none',
        WebkitTouchCallout: 'none'
      }}
      onCopy={(e) => e.preventDefault()}
      onCut={(e) => e.preventDefault()}
      onPaste={(e) => e.preventDefault()}
    >
      {/* Watermark overlay - more visible for deterrence */}
      <div 
        className="fixed inset-0 pointer-events-none z-50 overflow-hidden select-none"
        style={{ opacity: 0.06 }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-16" style={{ transform: 'rotate(-30deg)', transformOrigin: 'center' }}>
          {[...Array(80)].map((_, i) => (
            <span key={i} className="text-gray-900 text-xl font-bold whitespace-nowrap">
              CONFIDENTIAL • DO NOT COPY • OFFICIUM CONNECT
            </span>
          ))}
        </div>
      </div>

      {/* Secondary diagonal watermark pattern */}
      <div 
        className="fixed inset-0 pointer-events-none z-40 overflow-hidden select-none"
        style={{ opacity: 0.04 }}
        aria-hidden="true"
      >
        <div className="absolute inset-0 flex flex-wrap items-center justify-center gap-24" style={{ transform: 'rotate(30deg)', transformOrigin: 'center' }}>
          {[...Array(60)].map((_, i) => (
            <span key={i} className="text-red-900 text-lg font-semibold whitespace-nowrap">
              PROTECTED CONTENT • {new Date().toLocaleDateString()}
            </span>
          ))}
        </div>
      </div>

      {/* Security Notice */}
      <div className="max-w-3xl mx-auto mb-4">
        <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 flex items-center gap-3">
          <Shield className="h-5 w-5 text-amber-600 flex-shrink-0" />
          <p className="text-sm text-amber-700">
            This is a secure, read-only view. Screenshots and copying are disabled.
          </p>
        </div>
      </div>

      <div id="protected-content" className="max-w-3xl mx-auto transition-all duration-300">
        <Card className="shadow-xl border-0 overflow-hidden">
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-600 to-teal-600 p-6">
            <div className="flex items-center gap-3 text-white mb-4">
              <div className="h-12 w-12 bg-white/20 rounded-xl flex items-center justify-center">
                <FileText className="h-6 w-6" />
              </div>
              <div>
                <h1 className="text-xl font-bold">Interview Question</h1>
                <p className="text-white/80 text-sm">Officium Connect</p>
              </div>
            </div>
            
            <div className="flex flex-wrap gap-2">
              <Badge className={`${
                question.difficulty === 'Easy' ? 'bg-green-500' :
                question.difficulty === 'Medium' ? 'bg-amber-500' : 'bg-red-500'
              } text-white`}>
                {question.difficulty}
              </Badge>
              <Badge className="bg-white/20 text-white">{question.category}</Badge>
              {question.question_type === 'multiple_choice' && (
                <Badge className="bg-blue-500 text-white">Multiple Choice</Badge>
              )}
              {question.points > 1 && (
                <Badge className="bg-purple-500 text-white flex items-center gap-1">
                  <Award className="h-3 w-3" /> {question.points} pts
                </Badge>
              )}
              {question.time_limit && (
                <Badge className="bg-gray-500 text-white flex items-center gap-1">
                  <Clock className="h-3 w-3" /> {question.time_limit}s
                </Badge>
              )}
            </div>
          </div>

          <CardContent className="p-6 space-y-6">
            {/* Question Images - Multiple */}
            {((question.images && question.images.length > 0) || question.image_url) && (
              <div className="flex flex-wrap gap-3">
                {(question.images && question.images.length > 0 ? question.images : [question.image_url]).map((imgUrl, imgIdx) => (
                  <div key={imgIdx} className="rounded-xl overflow-hidden border-2 border-gray-100 shadow-md">
                    <img 
                      src={imgUrl.startsWith('http') ? imgUrl : `${API.replace('/api', '')}${imgUrl}`}
                      alt={`Question ${imgIdx + 1}`}
                      className="max-h-60 object-contain bg-gray-50"
                      draggable="false"
                      onDragStart={(e) => e.preventDefault()}
                    />
                  </div>
                ))}
              </div>
            )}

            {/* Question Text */}
            <div className="bg-gradient-to-r from-emerald-50 to-teal-50 border-2 border-emerald-200 rounded-xl p-5">
              {containsHtml(question.question) ? (
                <SafeHtmlRenderer 
                  html={question.question}
                  className="text-lg font-medium text-gray-800 leading-relaxed prose prose-lg max-w-none [&_u]:underline [&_font]:text-inherit"
                />
              ) : (
                <p className="text-lg font-medium text-gray-800 whitespace-pre-wrap leading-relaxed">
                  {question.question}
                </p>
              )}
            </div>

            {/* MCQ Options */}
            {question.question_type === 'multiple_choice' && question.options && (
              <div className="bg-blue-50 p-5 rounded-xl">
                <h4 className="font-semibold text-blue-700 mb-4 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Answer Options
                </h4>
                <div className="space-y-3">
                  {question.options.filter(opt => opt).map((opt, idx) => (
                    <div 
                      key={idx} 
                      className={`flex items-center gap-3 p-4 rounded-lg transition-all ${
                        question.correct_option === idx 
                          ? 'bg-green-100 border-2 border-green-400 shadow-md' 
                          : 'bg-white border border-gray-200'
                      }`}
                    >
                      <span className={`h-8 w-8 rounded-full flex items-center justify-center font-bold text-sm ${
                        question.correct_option === idx 
                          ? 'bg-green-500 text-white' 
                          : 'bg-gray-200 text-gray-600'
                      }`}>
                        {String.fromCharCode(65 + idx)}
                      </span>
                      <span className="text-gray-800 flex-1">{opt}</span>
                      {question.correct_option === idx && (
                        <CheckCircle className="h-5 w-5 text-green-600" />
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Expected Answer */}
            {question.expected_answer && (
              <div className="bg-green-50 p-5 rounded-xl border border-green-200">
                <h4 className="font-semibold text-green-700 mb-3 flex items-center gap-2">
                  <CheckCircle className="h-5 w-5" />
                  Expected Answer / Key Points
                </h4>
                {containsHtml(question.expected_answer) ? (
                  <SafeHtmlRenderer 
                    html={question.expected_answer}
                    className="text-green-600 leading-relaxed prose prose-sm max-w-none [&_u]:underline [&_font]:text-inherit"
                  />
                ) : (
                  <p className="text-green-600 whitespace-pre-wrap leading-relaxed">{question.expected_answer}</p>
                )}
              </div>
            )}

            {/* Tags */}
            {question.tags?.length > 0 && (
              <div className="flex flex-wrap gap-2 pt-2">
                {question.tags.map((tag, i) => (
                  <Badge key={i} variant="secondary" className="bg-purple-100 text-purple-700">
                    <Tag className="h-3 w-3 mr-1" />
                    {tag}
                  </Badge>
                ))}
              </div>
            )}

            {/* Footer */}
            <div className="text-sm text-gray-400 pt-4 border-t text-center">
              Shared via Officium Connect • Read-only access
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
